import React, { useState, useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import Modal from "../../../components/Modal";
import { EntregadorNaFila, PedidoExternoDetalhe } from "../types";

const SEGUNDOS_TIMER = 15;

interface ModalChamadaProps {
  isOpen: boolean;
  onClose: () => void;
  entregador: EntregadorNaFila;
  enderecos: number | "";
  onEnderecosChange?: (value: number | "") => void;
  onConfirmar?: (
    displayIds: number[],
    entregaExterna?: boolean,
    pedidosExternosDetalhes?: PedidoExternoDetalhe[]
  ) => void;
  onMoverFim?: () => void;
  onChamarNovamente?: () => void;
  usaIntegracaoPedidos?: boolean;
  numerosPermitidosNaFila?: Set<number>;
  isConfirmando?: boolean;
  /** Prefill travado do lote automático — sem digitar/alterar números */
  lockedDisplayIds?: number[];
}

const btnEnderecoBase =
  "flex h-10 w-10 items-center justify-center rounded-lg border-2 text-base font-bold transition-all disabled:cursor-not-allowed disabled:opacity-50 sm:h-11 sm:w-11";

const btnActionPrimary =
  "flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-dark disabled:cursor-not-allowed disabled:bg-neutral-400 sm:flex-1 sm:py-3";

const btnActionSecondary =
  "flex w-full items-center justify-center rounded-lg border px-3 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 sm:flex-1";

const ModalChamada: React.FC<ModalChamadaProps> = ({
  isOpen,
  onClose,
  entregador,
  enderecos,
  onEnderecosChange,
  onConfirmar,
  onMoverFim,
  onChamarNovamente,
  usaIntegracaoPedidos = false,
  numerosPermitidosNaFila,
  isConfirmando = false,
  lockedDisplayIds,
}) => {
  const [segundosRestantes, setSegundosRestantes] = useState(SEGUNDOS_TIMER);
  const [numerosPedidos, setNumerosPedidos] = useState<string[]>([]);
  const [quantidadePizzas, setQuantidadePizzas] = useState<string[]>([]);
  const [isEntregaExterna, setIsEntregaExterna] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const loteTravado =
    Array.isArray(lockedDisplayIds) && lockedDisplayIds.length > 0;

  const qty =
    typeof enderecos === "number" && enderecos >= 1 && enderecos <= 3
      ? enderecos
      : 0;

  useEffect(() => {
    if (loteTravado) {
      setNumerosPedidos(lockedDisplayIds!.map(String));
      setQuantidadePizzas(lockedDisplayIds!.map(() => ""));
      setIsEntregaExterna(false);
      return;
    }
    if (qty === 0 || !usaIntegracaoPedidos) {
      setNumerosPedidos([]);
      setQuantidadePizzas([]);
      return;
    }
    setNumerosPedidos((prev) => {
      const next = [...prev.slice(0, qty)];
      while (next.length < qty) next.push("");
      return next;
    });
    setQuantidadePizzas((prev) => {
      const next = [...prev.slice(0, qty)];
      while (next.length < qty) next.push("");
      return next;
    });
  }, [qty, usaIntegracaoPedidos, loteTravado, lockedDisplayIds]);

  const iniciarTimer = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setSegundosRestantes(SEGUNDOS_TIMER);
    intervalRef.current = setInterval(() => {
      setSegundosRestantes((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    if (!isOpen) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setSegundosRestantes(SEGUNDOS_TIMER);
      setIsEntregaExterna(false);
      setQuantidadePizzas([]);
      return;
    }
    iniciarTimer();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isOpen]);

  const handleChamarNovamente = () => {
    iniciarTimer();
    onChamarNovamente?.();
  };

  const displayIds = loteTravado
    ? lockedDisplayIds!
    : numerosPedidos
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => Number.isInteger(n) && n > 0);
  const semNumerosDuplicados =
    displayIds.length === 0 || new Set(displayIds).size === displayIds.length;
  const todosNumerosNaFila =
    numerosPermitidosNaFila === undefined ||
    (displayIds.length > 0 &&
      displayIds.every((n) => numerosPermitidosNaFila.has(n)));
  const quantidadesPizzasOk = quantidadePizzas.every((value) => {
    const raw = value.trim();
    if (!raw) return true;
    const n = parseInt(raw, 10);
    return Number.isInteger(n) && n > 0;
  });
  const numerosPedidosOk =
    displayIds.length === qty &&
    displayIds.every((n) => Number.isFinite(n) && n > 0) &&
    semNumerosDuplicados &&
    quantidadesPizzasOk &&
    (isEntregaExterna || todosNumerosNaFila);
  const canConfirm = loteTravado
    ? qty > 0 && displayIds.length === qty
    : usaIntegracaoPedidos
      ? qty > 0 && numerosPedidosOk
      : qty > 0;

  const buildPedidosExternosDetalhes = (): PedidoExternoDetalhe[] =>
    numerosPedidos.map((numero, index) => {
      const displayId = parseInt(numero.trim(), 10);
      const rawPizzas = quantidadePizzas[index]?.trim();
      const parsedPizzas = rawPizzas ? parseInt(rawPizzas, 10) : NaN;
      return {
        displayId,
        ...(Number.isInteger(parsedPizzas) && parsedPizzas > 0
          ? { quantidadePizzas: parsedPizzas }
          : {}),
      };
    });

  const handleConfirmar = () => {
    if (!canConfirm || !onConfirmar || isConfirmando) return;
    if (loteTravado) {
      void onConfirmar(lockedDisplayIds!, false);
      return;
    }
    if (usaIntegracaoPedidos && isEntregaExterna) {
      void onConfirmar(displayIds, true, buildPedidosExternosDetalhes());
      return;
    }
    void onConfirmar(usaIntegracaoPedidos ? displayIds : [], false);
  };

  const nomeCompleto = [entregador.nome, entregador.sobrenome]
    .filter(Boolean)
    .join(" ");

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Convocar Entregador"
      panelClassName="max-w-lg"
      bodyClassName="max-h-[calc(100vh-2rem)] space-y-3 overflow-y-auto p-4 sm:space-y-4 sm:p-5"
      compactHeader
    >
      {/* Entregador + timer inline */}
      <div className="flex items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2.5">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-base font-bold text-white sm:h-11 sm:w-11 sm:text-lg"
          aria-hidden
        >
          {entregador.posicao}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-bold text-neutral-900 sm:text-lg">
            {nomeCompleto}
          </p>
          <p className="flex items-center gap-1.5 text-xs text-neutral-500">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
            Será chamado para retirar
          </p>
        </div>
        {segundosRestantes > 0 && (
          <span
            className="shrink-0 rounded-full bg-white px-2.5 py-1 text-sm font-bold tabular-nums text-primary ring-1 ring-primary/20"
            aria-live="polite"
            aria-label={`${segundosRestantes} segundos restantes`}
          >
            {segundosRestantes}s
          </span>
        )}
      </div>

      {/* Endereços */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5">
        <div>
          <span className="text-sm font-semibold text-neutral-700">
            Endereços
          </span>
          {loteTravado ? (
            <p className="text-xs text-neutral-500">Definido pelo sistema</p>
          ) : null}
        </div>
        {loteTravado ? (
          <span className="rounded-lg border border-primary bg-primary px-3 py-1.5 text-sm font-bold text-white">
            {qty}
          </span>
        ) : (
          <div className="flex gap-2" role="group" aria-label="Quantidade de endereços">
            {[1, 2, 3].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => onEnderecosChange?.(num)}
                disabled={isConfirmando}
                aria-pressed={enderecos === num}
                className={`${btnEnderecoBase} ${
                  enderecos === num
                    ? "border-primary bg-primary text-white shadow-sm"
                    : "border-neutral-300 bg-white text-neutral-700 hover:border-primary hover:bg-primary/10"
                }`}
              >
                {num}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Entrega externa — logo abaixo de endereços */}
      {usaIntegracaoPedidos && qty > 0 && !loteTravado && (
        <div
          className={`rounded-lg border px-3 py-2.5 transition-colors ${
            isEntregaExterna
              ? "border-amber-300 bg-amber-50"
              : "border-neutral-200 bg-neutral-50"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <div className="min-w-0">
              <p
                className={`text-sm font-semibold ${
                  isEntregaExterna ? "text-amber-900" : "text-neutral-700"
                }`}
              >
                {isEntregaExterna
                  ? "Entrega Externa Ativada"
                  : "Entrega externa"}
              </p>
              {isEntregaExterna && (
                <p className="mt-0.5 text-xs text-amber-900/80">
                  Fora do Cardápio Web — não baixa a fila aguardando.
                </p>
              )}
            </div>
            <div
              className="flex shrink-0 rounded-lg border border-neutral-300 bg-white p-0.5"
              role="group"
              aria-label="Entrega externa"
            >
              <button
                type="button"
                onClick={() => {
                  setIsEntregaExterna(false);
                  setQuantidadePizzas([]);
                }}
                disabled={isConfirmando}
                aria-pressed={!isEntregaExterna}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                  !isEntregaExterna
                    ? "bg-neutral-800 text-white shadow-sm"
                    : "text-neutral-600 hover:bg-neutral-100"
                }`}
              >
                Não
              </button>
              <button
                type="button"
                onClick={() => setIsEntregaExterna(true)}
                disabled={isConfirmando}
                aria-pressed={isEntregaExterna}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                  isEntregaExterna
                    ? "bg-amber-600 text-white shadow-sm"
                    : "text-neutral-600 hover:bg-neutral-100"
                }`}
              >
                Sim
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pedidos */}
      {loteTravado && displayIds.length > 0 && (
        <div className="space-y-2.5 rounded-lg border border-primary/20 bg-primary/5 p-3">
          <div>
            <p className="text-sm font-semibold text-neutral-800">
              Pedidos do lote
            </p>
            <p className="text-xs text-neutral-500">
              Definido pelo Roteirista automático — sem alteração manual.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {displayIds.map((id) => (
              <span
                key={id}
                className="inline-flex items-center rounded-md bg-white px-3 py-1.5 text-sm font-bold text-neutral-900 shadow-sm ring-1 ring-neutral-200"
              >
                #{id}
              </span>
            ))}
          </div>
        </div>
      )}

      {usaIntegracaoPedidos && qty > 0 && !loteTravado && (
        <div className="space-y-2.5 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
          <div>
            <p className="text-sm font-semibold text-neutral-800">Pedidos</p>
            <p className="text-xs text-neutral-500">
              {isEntregaExterna
                ? "Um pedido por endereço. Pizzas opcionais por endereço."
                : "Só pedidos 100% prontos na fila aguardando (ex.: 3/3)."}
            </p>
          </div>

          <div className="space-y-2">
            {numerosPedidos.map((value, index) => (
              <div
                key={index}
                className={
                  isEntregaExterna
                    ? "grid grid-cols-1 gap-2 rounded-md border border-amber-200/80 bg-amber-50/40 p-2 sm:grid-cols-[3.5rem_1fr_4.5rem] sm:items-center sm:gap-x-2"
                    : "grid grid-cols-1 gap-1 sm:grid-cols-[3.5rem_1fr] sm:items-center sm:gap-x-2"
                }
              >
                <span className="text-xs font-semibold text-neutral-600 sm:text-right">
                  {isEntregaExterna ? `End. ${index + 1}` : `#${index + 1}`}
                </span>
                <input
                  type="number"
                  min={1}
                  value={value}
                  disabled={isConfirmando}
                  aria-label={`Número do pedido ${index + 1}`}
                  onChange={(e) => {
                    const next = [...numerosPedidos];
                    next[index] = e.target.value.replace(/\D/g, "").trim();
                    setNumerosPedidos(next);
                  }}
                  placeholder="Nº pedido"
                  className="w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
                {isEntregaExterna && (
                  <input
                    type="number"
                    min={1}
                    value={quantidadePizzas[index] ?? ""}
                    disabled={isConfirmando}
                    aria-label={`Quantidade de pizzas endereço ${index + 1}`}
                    onChange={(e) => {
                      const next = [...quantidadePizzas];
                      next[index] = e.target.value.replace(/\D/g, "").trim();
                      setQuantidadePizzas(next);
                    }}
                    placeholder="Pizzas"
                    className="w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500 sm:max-w-[4.5rem]"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ações */}
      <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:gap-2">
        <button
          type="button"
          onClick={handleConfirmar}
          disabled={!canConfirm || isConfirmando}
          className={btnActionPrimary}
        >
          {isConfirmando ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Confirmando...
            </>
          ) : (
            "Confirmar Saída"
          )}
        </button>
        <button
          type="button"
          onClick={handleChamarNovamente}
          disabled={isConfirmando}
          className={`${btnActionSecondary} border-primary/40 bg-primary/5 text-primary hover:bg-primary/10`}
        >
          Chamar de novo
        </button>
        <button
          type="button"
          onClick={onMoverFim}
          disabled={isConfirmando}
          className={`${btnActionSecondary} border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50`}
        >
          Fim da fila
        </button>
      </div>
    </Modal>
  );
};

export default ModalChamada;
